import React, { useEffect, useMemo, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation } from "@tanstack/react-query"
// MUI components 
import { Typography } from "@mui/material";
// components 
import ComparisonPeriodPicker from "../../components/DatePicker/ComparisonPeriodPicker";
import SelectDropdown from "../../components/SelectDropdown";
import Table from "../../components/Table";
import NoData from "../../components/NoData";
import ErrorText from "../../components/ErrorText";
import { PDFButton } from "../../components/PdfComponent/PDFButton";
import { ShimmerComponent } from "../../components/Shimmer/ShimmerComponent";
import BouncingLogo from "../../components/Shimmer/BouncingLogo";
// constants and functions 
import { OVERALL_PERFORMANCE, TABLE_YTD, COMPARISON_PERIOD } from "../../constants/datePickerConstants";
import { PDFNames, verifySpendAndOppsRow } from "../../constants/utils";
import { columnsArrayYTD, performanceOverViewTexts, configureRowsHandler, fetchCurrentToComparisonYearData, compareQuarterAndYearValueHandlerForTableOne, quarterAndYearValueHandler, comparisonPeriodYTD, timePeriodRangeHandler, timePeriodYTDRange, comparisonPeriodRangeHandler, storeFilters, fetchWeeklyOppsData, fetchColumnHeaders, transformData, transformHeaders, fetchOppsSummary, transformOppsHeaders, tranformOppsRows, YTD_KEY_PERF, SPEND_KEY_PERF, HEADER_KEY_PERF} from "../PerformanceOverview/services/PerformanceOverviewServices";
import { formatNumberUsingUnit } from "../../utils/formatUtils";
import selectors from "../../modules/selectors/datePickerSelectors";
import { getColorStyleForCell } from '../../utils/colorSchmeOverallPerformance';
// styles 
import branchPerformanceStyles from "../../styles/branchPerformanceStyles";
import {filterValidation, findWeekAndMonth } from "../../data/branchPerformanceData";

const weeklyOppsHeader = { Header: () => (<span style={{ width: '150px', display: 'inline-block' }}></span>), accessor: 'Weekly_Opps_Seasonality' };
const getOppsHeaders = (tableHeaders) => {
  return [weeklyOppsHeader, ...tableHeaders];
}
const PerformanceOverview = () => {
  const pdfRef = useRef();
  const [currentToCompareYearDataArray, setCurrentToCompareYearDataArray] = useState([]);
  const { userStores, userData } = useSelector((state) => state?.auth);
  const tableId = userData?.data?.data_sources?.find(data => data?.name === "branch_performance_overview")?.identifier;
  const tableIdForTableTwo = userData?.data?.data_sources?.find(data => data?.name === "vw_spend_and_opps_tab")?.identifier;
  const options = useMemo(() => {
    const stores = userStores.map(store => ({
      label: store?.branch_name,
      value: store?.branch_number,
      ...store,
    }));
    const GaleMarkets = stores.filter(store => store.value === 1);
    if (GaleMarkets?.length) {
      return [GaleMarkets[0], ...stores.filter(store => store.value !== 1).sort((a, b) => a.label.localeCompare(b.label))]
    } else {
      return [...stores.filter(store => store.value !== 1).sort((a, b) => a.label.localeCompare(b.label))];
    }
  }, [userStores])
  const { calendarDataWithDate } = useSelector(state => state.calendar)
  const { currentQuarter, overallPerformanceRange, compareOverallPerformanceRangeYTD, overallPerformanceYTDRange } = useSelector(state => state.overallPerformance);
  const { transformedData, transformedDataWithDate } = useSelector(selectors.datePickerCombinedData);
  const [storeId, setStoreId] = useState(options?.[0]?.value);
  const [resetStore, setResetStore] = useState(false);
  const [compareFilter, setCompareFilter] = useState(COMPARISON_PERIOD);
  const [currentYtdFilters, setCurrentYtdFilters] = useState([]);
  const [compareYtdFilters, setCompareYtdFilters] = useState([]);
  const [oppsSummaryRowData, setoppsSummaryRowData] = useState([]);
  const [oppsSummaryTableHeaders, setoppsSummaryTableHeaders] = useState([]);
  const [rowData, setRowData] = useState([]);
  const [tableHeaders, setTableHeaders] = useState([]);
  const currentYear = transformedDataWithDate[0]?.currYear;
  const dispatch = useDispatch();
  const handleStoreIdChange = (store) => {
    setStoreId(store)
    setCurrentToCompareYearDataArray([])
  };

  const handleResetForStore = () => {
    setResetStore(true)
  };

  const {
    data: oppsSummaryData,
    mutate: oppsSummaryMutation,
    isLoading: isLoadingOppsSummary,
  } = useMutation({
    mutationFn: fetchOppsSummary
  })

  const {
    data: weeklyOppsData,
    mutate: weeklyOppsMutation,
    isLoading: isLoadingWeeklyOppsData
  } = useMutation({
    mutationFn: fetchWeeklyOppsData
  })

  const {
    data: columnHeadersData,
    mutate: columnHeadersMutation,
    isLoading: isLoadingComparisionHeaders,
  } = useMutation({
    mutationFn: fetchColumnHeaders
  })

  const {
    data: currentToComparisonYearData,
    mutate: currentToComparisonYearMutation,
    isLoading: isLoadingCurrentToComparisonYearData,
    isSuccess: isSuccessCurrentToComparisonYearData
  } = useMutation({
    mutationFn: fetchCurrentToComparisonYearData
  })

  const columnsYTD = useMemo(() => {
    return [...columnsArrayYTD, {
      Header: <ComparisonPeriodPicker component={OVERALL_PERFORMANCE} comparePeriod={compareFilter} tableType={TABLE_YTD} branchPerformanceButtonStyle />,
      accessor: 'percentToPrevYear',
      Cell: ({ cell }) => <span>{formatNumberUsingUnit(cell.value, "%")}</span>
    }]
  }, []);

  useEffect(() => {
    if (transformedDataWithDate) {
      const currentWeekExtraction = parseInt(findWeekAndMonth(transformedDataWithDate)?.week?.slice(1));
      const currentWeek = String(currentWeekExtraction)?.padStart(2, '0');
      const oppsFilter = `${currentYear - 2}${currentWeek}`;
      !isLoadingOppsSummary && filterValidation(oppsFilter) && oppsSummaryMutation({ storeId, tableId, oppsFilter, YTD_KEY_PERF})
    }
  }, [storeId, transformedDataWithDate]);

  useEffect(() => {
    if (oppsSummaryData) {
      const tranformOppsTableHeaders = transformOppsHeaders(oppsSummaryData, transformedDataWithDate, userData?.data?.current_year);
      const tranformOppsTableRow = oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result?.length > 0 && tranformOppsRows(oppsSummaryData, transformedDataWithDate, userData?.data?.current_year);
      tranformOppsTableRow?.length > 0 && setoppsSummaryRowData(tranformOppsTableRow);
      setoppsSummaryTableHeaders(tranformOppsTableHeaders);
    }
  }, [oppsSummaryData, storeId]);

  useEffect(() => {
    if (columnHeadersData?.data?.performance_overview_header_spends_query?.result) {
      if (weeklyOppsData?.data?.performance_overview_opps_summary_spend_query?.result) {
        const transformedTableHeaders = transformHeaders(columnHeadersData.data, storeId, transformedDataWithDate, weeklyOppsData?.data);
        if (weeklyOppsData) {
          const transformedOppsData = transformData(weeklyOppsData.data, 'opps_data', storeId, transformedDataWithDate);
          const verifiedOppsData = transformedOppsData?.map(row => verifySpendAndOppsRow(row, transformedTableHeaders, storeId));
          if (verifiedOppsData[0]) {
            setTableHeaders(transformedTableHeaders);
            setRowData(verifiedOppsData);
          }
        }
      }
    }
  }, [weeklyOppsData, columnHeadersData, storeId, oppsSummaryData]);

  useEffect(() => {
    if (transformedDataWithDate) {
      const yearFilter = currentYear - 1;
      !isLoadingWeeklyOppsData && yearFilter && weeklyOppsMutation({ storeId, tableIdForTableTwo, yearFilter, SPEND_KEY_PERF});
      !isLoadingComparisionHeaders && columnHeadersMutation({ storeId, tableIdForTableTwo, HEADER_KEY_PERF});
    }
  }, [storeId, transformedDataWithDate]);

  useEffect(() => {
    if (compareOverallPerformanceRangeYTD) {
      storeFilters(storeId, setCompareYtdFilters, setCurrentYtdFilters, compareOverallPerformanceRangeYTD, overallPerformanceYTDRange);
    }
  }, [storeId, overallPerformanceRange, compareOverallPerformanceRangeYTD, overallPerformanceYTDRange])

  useEffect(() => {
    timePeriodRangeHandler(overallPerformanceRange, transformedDataWithDate, transformedData, dispatch);
    timePeriodYTDRange(transformedDataWithDate, dispatch);
  }, [transformedData, calendarDataWithDate, transformedDataWithDate])

  useEffect(() => {
    comparisonPeriodYTD(overallPerformanceYTDRange, dispatch);
    comparisonPeriodRangeHandler(overallPerformanceRange, dispatch, setCompareFilter);
  }, [transformedData, calendarDataWithDate, overallPerformanceYTDRange, overallPerformanceRange])

  useEffect(() => {
    quarterAndYearValueHandler(transformedDataWithDate, dispatch);
  }, [currentQuarter, currentYear, transformedDataWithDate])

  useEffect(() => {
    compareQuarterAndYearValueHandlerForTableOne(overallPerformanceRange, transformedDataWithDate, transformedData, dispatch);
  }, [overallPerformanceRange, transformedDataWithDate])

  useEffect(() => {
    if (currentYtdFilters?.length > 0 && compareYtdFilters?.length > 0 && tableId) {
      currentToComparisonYearMutation({ currentYtdFilters, compareYtdFilters, tableId })
    }
  }, [JSON.stringify(compareYtdFilters), JSON.stringify(currentYtdFilters)])

  useEffect(() => {
    const currentToCompareYear = currentToComparisonYearData?.data;
    if (isSuccessCurrentToComparisonYearData) {
      const result = currentToCompareYear?.map(storeData => configureRowsHandler(storeData))
      storeId && setCurrentToCompareYearDataArray(result?.[0]?.rows)
    }
  }, [isSuccessCurrentToComparisonYearData, currentToComparisonYearData]);

  return ((userStores?.length > 0) ?
    <div ref={pdfRef} style={branchPerformanceStyles?.performanceContainer}>
            <div style={{ width: "90%", textAlign: 'left', margin: 'auto', fontSize: "1.5rem", fontWeight: "500", lineHeight: "1.6", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{performanceOverViewTexts?.headline}</span>
            </div>
      <div style={branchPerformanceStyles?.performanceDropdwonContainer}>
        <div>
          <SelectDropdown
            options={options}
            label={performanceOverViewTexts?.branchDropDownLabel}
            resetValues={resetStore}
            handleResetValues={handleResetForStore}
            singleSelectDefaultValue={options[0]?.value}
            width={'200px'}
            handleFilterChange={handleStoreIdChange}
            marginExist={false}
            disabled={isLoadingOppsSummary || isLoadingCurrentToComparisonYearData || isLoadingWeeklyOppsData || isLoadingComparisionHeaders}
          />
        </div>
        <PDFButton
          disabled={isLoadingOppsSummary || isLoadingCurrentToComparisonYearData || isLoadingWeeklyOppsData || isLoadingComparisionHeaders}
          pdfRef={pdfRef} pdfName={PDFNames.performanceOverview}
        />
      </div>
      {isLoadingOppsSummary || isLoadingCurrentToComparisonYearData || isLoadingWeeklyOppsData || isLoadingComparisionHeaders ? (
        <ShimmerComponent updatedStyle={{ width: '90%', height: '100%', backgroundSize: '2000px 800px', border: '0px', margin: "auto", padding: "0 20px"}} >
         <BouncingLogo height='72vh' text='Loading...' />
        </ShimmerComponent>) : <>
        <div style={branchPerformanceStyles?.performanceOppsSummaryCont}>
          <Typography variant="h6" align='left' >
            {performanceOverViewTexts?.oppsSummary}
          </Typography>
          {oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result && oppsSummaryData?.data?.performance_overview_opps_summary_YTD_query?.result?.length > 0 &&
            <div style={branchPerformanceStyles?.oppsSummaryStyle} ><Table boldHeaders={true} boldDataColumns={['_Heading']} data={oppsSummaryRowData} columns={oppsSummaryTableHeaders} /></div>}
          {rowData?.length > 0 ?
            <div style={branchPerformanceStyles?.spendOppsStyle}>
              <Table boldHeaders={true} boldDataColumns={['Weekly_Opps_Seasonality']} data={rowData} columns={getOppsHeaders(tableHeaders)} /></div> : <NoData />}
        </div>
        <div style={branchPerformanceStyles?.performanceOppsSummaryCont}>
          <Typography variant="h6" align='left' >
            {performanceOverViewTexts?.ytdSummary}
          </Typography>
          {currentToCompareYearDataArray?.length > 0 ?
            <Table
              data={currentToCompareYearDataArray}
              columns={columnsYTD}
              branchPerformanceStyle
              getCellProps={cellInfo => {
                if (['percentToPlan', 'percentToPrevYear'].includes(cellInfo.column.id)) {
                  return {
                    style: {
                      ...getColorStyleForCell(cellInfo.row.original.viewData, cellInfo.value)
                    }
                  }
                } else { return { style: {} } }
              }}
            /> : <NoData />}
        </div>
      </>}
    </div> : <div style={branchPerformanceStyles?.errorContainerStyle}>
      <div style={branchPerformanceStyles?.errorSubcont}>
        <div style={branchPerformanceStyles?.errorTextContainer}>
          <ErrorText reason='No branches assigned to user' />
        </div>
      </div>
    </div>)
}

export default PerformanceOverview;